import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

interface CalendarWorkout {
  id: string;
  name: string;
  category?: string | null;
  scoring_type: string;
  scheduled_date: string;
}

interface CalendarViewProps {
  workouts: CalendarWorkout[];
  loading?: boolean;
  initialYear?: number;
  initialMonth?: number;
  onDayClick?: (date: string, workouts: CalendarWorkout[]) => void;
  onWorkoutClick?: (workout: CalendarWorkout) => void;
  onMonthChange?: (year: number, month: number) => void;
}

const categoryColors: Record<string, string> = {
  hybrid: 'bg-purple-500',
  dual_erg: 'bg-blue-500',
  single_erg: 'bg-cyan-500',
  medley: 'bg-orange-500',
  team_relay: 'bg-green-500',
  endurance: 'bg-red-500',
  standard: 'bg-yellow-500',
  short_sprint: 'bg-rose-500',
  long_sprint: 'bg-amber-500',
  middle_distance: 'bg-emerald-500',
  enduro_sprint: 'bg-violet-500',
  enduro_long: 'bg-fuchsia-500',
  enduro_medley: 'bg-teal-500',
};

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MAX_VISIBLE_WORKOUTS_PER_DAY = 2;

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  // Returns 0=Mon, 1=Tue, ... 6=Sun
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1;
}

function formatDate(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export function CalendarView({
  workouts,
  loading,
  initialYear,
  initialMonth,
  onDayClick,
  onWorkoutClick,
  onMonthChange,
}: CalendarViewProps) {
  const today = new Date();
  const [year, setYear] = useState(initialYear ?? today.getFullYear());
  const [month, setMonth] = useState(initialMonth ?? today.getMonth());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  // Fire onMonthChange only when user navigates months (not on initial mount)
  // Use ref to avoid infinite re-render loop from unstable callback references
  const onMonthChangeRef = useRef(onMonthChange);
  onMonthChangeRef.current = onMonthChange;
  const isMounted = useRef(false);
  useEffect(() => {
    if (isMounted.current) {
      onMonthChangeRef.current?.(year, month);
    } else {
      isMounted.current = true;
    }
  }, [year, month]);

  // Index workouts by date (normalize to YYYY-MM-DD to handle any format)
  const workoutsByDate = useMemo(() => {
    const map = new Map<string, CalendarWorkout[]>();
    for (const w of workouts) {
      const date = w.scheduled_date?.slice(0, 10);
      if (!date) continue;
      const existing = map.get(date);
      if (existing) {
        existing.push(w);
      } else {
        map.set(date, [w]);
      }
    }
    return map;
  }, [workouts]);

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const goToPrevMonth = () => {
    setSelectedDate(null);
    setMonth((m) => (m === 0 ? 11 : m - 1));
    if (month === 0) setYear((y) => y - 1);
  };

  const goToNextMonth = () => {
    setSelectedDate(null);
    setMonth((m) => (m === 11 ? 0 : m + 1));
    if (month === 11) setYear((y) => y + 1);
  };

  const goToToday = () => {
    setSelectedDate(null);
    setMonth(today.getMonth());
    setYear(today.getFullYear());
  };

  const handleDayClick = (day: number) => {
    const dateStr = formatDate(year, month, day);
    setSelectedDate(dateStr);
    const dayWorkouts = workoutsByDate.get(dateStr) || [];
    onDayClick?.(dateStr, dayWorkouts);
  };

  const todayStr = formatDate(today.getFullYear(), today.getMonth(), today.getDate());

  // Build calendar grid
  const cells: Array<{ day: number | null; date: string | null }> = [];
  // Empty cells before first day
  for (let i = 0; i < firstDay; i++) {
    cells.push({ day: null, date: null });
  }
  // Day cells
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, date: formatDate(year, month, d) });
  }
  const hasWorkoutsInMonth = cells.some(
    (cell) => cell.date && (workoutsByDate.get(cell.date)?.length ?? 0) > 0
  );

  // Selected day's workouts
  const selectedWorkouts = selectedDate ? workoutsByDate.get(selectedDate) || [] : [];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="grid w-full grid-cols-[auto_1fr_auto] items-center gap-2 sm:w-auto">
          <Button variant="outline" size="sm" onClick={goToPrevMonth} aria-label="Previous month">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h3 className="min-w-0 text-center text-base font-semibold sm:min-w-[180px] sm:text-lg">
            {MONTH_NAMES[month]} {year}
          </h3>
          <Button variant="outline" size="sm" onClick={goToNextMonth} aria-label="Next month">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <Button variant="ghost" size="sm" onClick={goToToday} className="self-end sm:self-auto">
          Today
        </Button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-px">
        {DAYS.map((day) => (
          <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div
        aria-busy={loading}
        className={`grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden transition-opacity duration-200 ${loading ? 'opacity-50' : ''}`}
      >
        {cells.map((cell, idx) => {
          if (!cell.day || !cell.date) {
            // biome-ignore lint/suspicious/noArrayIndexKey: empty calendar cells have no unique id
            return <div key={idx} className="h-24 bg-card sm:h-28 md:h-32" />;
          }
          const cellDay = cell.day;
          const cellDate = cell.date;
          const dayWorkouts = workoutsByDate.get(cellDate) || [];
          const isToday = cellDate === todayStr;
          const isSelected = cellDate === selectedDate;

          return (
            <button
              key={cellDate}
              type="button"
              onClick={() => handleDayClick(cellDay)}
              className={`h-24 overflow-hidden bg-card p-1 text-left transition-colors hover:bg-accent/50 sm:h-28 sm:p-2 md:h-32 ${
                isSelected ? 'ring-2 ring-primary ring-inset' : ''
              }`}
            >
              <span
                className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-medium sm:h-6 sm:w-6 sm:text-xs ${
                  isToday ? 'bg-primary text-primary-foreground' : 'text-foreground'
                }`}
              >
                {cell.day}
              </span>
              {/* Workout indicators */}
              {dayWorkouts.length > 0 && (
                <div
                  className="mt-1 grid w-fit grid-cols-[1.25rem_auto] items-center sm:hidden"
                  aria-label={`${dayWorkouts.length} ${dayWorkouts.length === 1 ? 'workout' : 'workouts'}`}
                >
                  <div
                    className={`h-2 w-2 shrink-0 justify-self-center rounded-full ${
                      categoryColors[dayWorkouts[0]?.category || ''] || 'bg-primary'
                    }`}
                  />
                  <span className="text-[10px] font-medium text-muted-foreground">
                    {dayWorkouts.length}
                  </span>
                </div>
              )}
              <div className="mt-1 hidden space-y-1 overflow-hidden sm:block">
                {dayWorkouts.slice(0, MAX_VISIBLE_WORKOUTS_PER_DAY).map((w) => (
                  <div key={w.id} className="flex min-w-0 items-start gap-1" title={w.name}>
                    <div
                      className={`mt-0.5 h-2 w-2 flex-shrink-0 rounded-full ${
                        categoryColors[w.category || ''] || 'bg-primary'
                      }`}
                    />
                    <span className="line-clamp-2 break-words text-[11px] leading-tight text-foreground/80">
                      {w.name}
                    </span>
                  </div>
                ))}
                {dayWorkouts.length > MAX_VISIBLE_WORKOUTS_PER_DAY && (
                  <span className="block text-[11px] font-medium leading-tight text-muted-foreground">
                    +{dayWorkouts.length - MAX_VISIBLE_WORKOUTS_PER_DAY} more
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {!loading && !hasWorkoutsInMonth && (
        <p className="text-center text-sm text-muted-foreground">
          No workouts scheduled for this month.
        </p>
      )}

      {/* Selected day detail */}
      {selectedDate && (
        <div className="rounded-lg border border-border p-4 space-y-2">
          <h4 className="text-sm font-semibold">
            {new Date(`${selectedDate}T00:00:00`).toLocaleDateString('en-GB', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </h4>
          {selectedWorkouts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No workouts scheduled</p>
          ) : (
            <div className="space-y-2">
              {selectedWorkouts.map((w) => (
                <button
                  key={w.id}
                  type="button"
                  onClick={() => onWorkoutClick?.(w)}
                  className="w-full flex items-center gap-3 rounded-md border border-border p-2 text-left hover:bg-accent/50 transition-colors"
                >
                  <div
                    className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      categoryColors[w.category || ''] || 'bg-primary'
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <span className="block break-words text-sm font-medium">{w.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {w.category || 'Uncategorized'}
                    </span>
                  </div>
                  <Badge variant="outline" className="text-xs flex-shrink-0">
                    {w.scoring_type}
                  </Badge>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
